import {
  type AddResourceInput,
  type CreateActionInput,
  createMission,
  createMissionAction,
  createMissionAttendance,
  type CreateMissionInput,
  createMissionMapElement,
  createMissionPatient,
  createMissionReport,
  createMissionResource,
  createMissionSection,
  type CreateReportInput,
  endMission,
  type File,
  getFile,
  getReport,
  type Mission,
  type MissionReportSearchResults,
  type MutationBase,
  type MutationMeta,
  type PatientUpdate,
  removeMissionAttendance,
  removeMissionMapElement,
  removeMissionPatient,
  removeMissionResource,
  removeMissionSection,
  reopenMission,
  requestFileUpload,
  searchReports,
  type SetAttendanceInput,
  type SetMapElementInput,
  type SetSectionInput,
  updateMission,
  updateMissionAttendance,
  type UpdateMissionInput,
  updateMissionPatient,
  updateMissionReportData,
  type UpdateMissionReportData,
  updateMissionResource,
  updateMissionSection,
  type UpdateResourceInput,
  getUser,
  type UserInfo,
} from "@rescuetablet/missions-client";
import { nanoid } from "nanoid";

export type Options = {
  baseUrl?: string;
  headers?:
    | RequestInit["headers"]
    | Record<
        string,
        | string
        | number
        | boolean
        | (string | number | boolean)[]
        | null
        | undefined
        | unknown
      >;
  auth?:
    | ((auth: any) => Promise<string | undefined> | string | undefined)
    | string
    | undefined;
};

export class MissionsClient {
  constructor(
    private readonly options: Options,
    private readonly meta: MutationMeta = {},
  ) {}

  async createMission(values: CreateMissionInput): Promise<Mission> {
    return (
      await createMission({
        ...this.options,
        path: { missionId: crypto.randomUUID() },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  /** The Missions user with its group permissions as the API checks them. */
  async getUser(): Promise<UserInfo> {
    return (await getUser({ ...this.options, throwOnError: true })).data;
  }

    async reopenMission(missionId: string): Promise<Mission> {
    return (
      await reopenMission({
        ...this.options,
        path: { missionId },
        throwOnError: true,
      })
    ).data;
  }

  async updateMission(
    missionId: string,
    values: UpdateMissionInput,
  ): Promise<Mission> {
    return (
      await updateMission({
        ...this.options,
        path: { missionId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async createMissionAction(
    missionId: string,
    values: CreateActionInput,
  ): Promise<Mission> {
    return (
      await createMissionAction({
        ...this.options,
        path: { missionId, actionId: nanoid() },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async createMissionReport(
    missionId: string,
    values: CreateReportInput,
  ): Promise<Mission> {
    return (
      await createMissionReport({
        ...this.options,
        path: { missionId, reportId: nanoid() },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async createMissionResource(
    missionId: string,
    values: AddResourceInput,
  ): Promise<Mission> {
    return (
      await createMissionResource({
        ...this.options,
        path: { missionId, resourceId: nanoid() },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async updateMissionResource(
    missionId: string,
    resourceId: string,
    values: UpdateResourceInput,
  ): Promise<Mission> {
    return (
      await updateMissionResource({
        ...this.options,
        path: { missionId, resourceId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async setMissionResource(
    missionId: string,
    resourceId: string,
    values: AddResourceInput,
  ): Promise<Mission> {
    return (
      await createMissionResource({
        ...this.options,
        path: { missionId, resourceId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async removeMissionResource(
    missionId: string,
    resourceId: string,
  ): Promise<Mission> {
    return (
      await removeMissionResource({
        ...this.options,
        path: { missionId, resourceId },
        throwOnError: true,
      })
    ).data;
  }

  async endMission(missionId: string): Promise<void> {
    return (
      await endMission({
        ...this.options,
        path: { missionId },
        throwOnError: true,
      })
    ).data;
  }

  async createMissionMapElement(
    missionId: string,
    values: SetMapElementInput,
  ): Promise<Mission> {
    return (
      await createMissionMapElement({
        ...this.options,
        path: { missionId, elementId: nanoid() },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async updateMissionMapElement(
    missionId: string,
    elementId: string,
    values: SetMapElementInput,
  ): Promise<Mission> {
    return (
      await createMissionMapElement({
        ...this.options,
        path: { missionId, elementId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async deleteMissionMapElement(
    missionId: string,
    elementId: string,
  ): Promise<Mission> {
    return (
      await removeMissionMapElement({
        ...this.options,
        path: { missionId, elementId },
        throwOnError: true,
      })
    ).data;
  }

  async createSection(
    missionId: string,
    sectionId: string,
    values: SetSectionInput,
  ): Promise<Mission> {
    return (
      await createMissionSection({
        ...this.options,
        path: { missionId, sectionId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async updateSection(
    missionId: string,
    sectionId: string,
    values: SetSectionInput,
  ): Promise<Mission> {
    return (
      await updateMissionSection({
        ...this.options,
        path: { missionId, sectionId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async deleteSection(missionId: string, sectionId: string): Promise<Mission> {
    return (
      await removeMissionSection({
        ...this.options,
        path: { missionId, sectionId },
        throwOnError: true,
      })
    ).data;
  }

  async createPatient(
    missionId: string,
    patientId: string,
    values: PatientUpdate,
  ): Promise<Mission> {
    return (
      await createMissionPatient({
        ...this.options,
        path: { missionId, patientId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async updatePatient(
    missionId: string,
    patientId: string,
    values: PatientUpdate,
  ): Promise<Mission> {
    return (
      await updateMissionPatient({
        ...this.options,
        path: { missionId, patientId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async deletePatient(missionId: string, patientId: string): Promise<Mission> {
    return (
      await removeMissionPatient({
        ...this.options,
        path: { missionId, patientId },
        throwOnError: true,
      })
    ).data;
  }

  async createAttendance(
    missionId: string,
    attendanceId: string,
    values: SetAttendanceInput,
  ): Promise<Mission> {
    return (
      await createMissionAttendance({
        ...this.options,
        path: { missionId, attendanceId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async updateAttendance(
    missionId: string,
    attendanceId: string,
    values: SetAttendanceInput,
  ): Promise<Mission> {
    return (
      await updateMissionAttendance({
        ...this.options,
        path: { missionId, attendanceId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async deleteAttendance(
    missionId: string,
    attendanceId: string,
  ): Promise<Mission> {
    return (
      await removeMissionAttendance({
        ...this.options,
        path: { missionId, attendanceId },
        throwOnError: true,
      })
    ).data;
  }

  async updateReportData(
    missionId: string,
    reportId: string,
    values: UpdateMissionReportData,
  ): Promise<Mission> {
    return (
      await updateMissionReportData({
        ...this.options,
        path: { missionId, reportId },
        body: this.withMeta(values),
        throwOnError: true,
      })
    ).data;
  }

  async uploadFile(missionId: string, file: Blob): Promise<File> {
    const fileId = nanoid();

    const {
      data: { url },
    } = await requestFileUpload({
      ...this.options,
      path: { missionId, fileId },
      body: { type: file.type, size: file.size },
      throwOnError: true,
    });

    const response = await fetch(url, { method: "PUT", body: file });
    if (!response.ok) {
      throw new Error(`Error uploading file: ${response.statusText}`);
    }

    return { id: fileId, type: file.type, size: file.size };
  }

  async getFileURL(missionId: string, fileId: string): Promise<string> {
    const {
      data: { url },
    } = await getFile({
      ...this.options,
      path: { missionId, fileId },
      query: { noRedirect: true },
      throwOnError: true,
    });

    return url;
  }

  async getReports(
    search: string,
    limit?: number,
  ): Promise<MissionReportSearchResults> {
    return (
      await searchReports({
        ...this.options,
        query: { search, limit },
        throwOnError: true,
      })
    ).data;
  }

  async getReport(
    reportId: string,
    response: "redirect" | "url" | "data" = "data",
    contentType: "application/json" | "application/pdf" | "application/zip",
  ): Promise<unknown> {
    return (
      await getReport({
        ...this.options,
        path: { missionId: reportId },
        query: { response },
        headers: { accept: contentType },
        throwOnError: true,
      })
    ).data;
  }

  private withMeta<T extends MutationBase>(mutation: T): T {
    return { ...mutation, meta: { ...this.meta, ...mutation.meta } } as T;
  }
}
